import { InfoOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import TimeExclusionPopup from "./TimeExclusionPopup.component";

/**
 * Uncovered Items (TIME) — one editor popup per letter (T/I/M/E).
 * Maps to warranty_exclusion + warranty_exclusion_column.
 */
const TimeWarranty = ({ time, setWarranty, sectionKey }) => {
  const items = time?.fields ?? [];
  const [openLetter, setOpenLetter] = useState(null);

  const activeItem = items.find(
    (item) => (item.letter || item.uncoverage_type) === openLetter
  );

  const updateItem = (itemId, patch) => {
    setWarranty((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        time: {
          ...prev[sectionKey].time,
          fields: prev[sectionKey].time.fields.map((item) =>
            item.id === itemId ? { ...item, ...patch } : item
          ),
        },
      },
    }));
  };

  const hasContent = (item) =>
    Boolean(
      item?.title_en?.trim?.() ||
        item?.title_es?.trim?.() ||
        item?.footer_en?.trim?.() ||
        item?.footer_es?.trim?.() ||
        item?.what_to_know_text_en?.trim?.() ||
        item?.columns?.some(
          (col) =>
            col.column_header_en?.trim?.() ||
            col.column_items_en?.trim?.() ||
            col.column_header_es?.trim?.() ||
            col.column_items_es?.trim?.()
        )
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Uncovered Items (TIME)
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Click a letter to edit the uncovered-items popup customers see for that
        letter.
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {items.map((item) => {
          const letter = item.letter || item.uncoverage_type || "—";
          const filled = hasContent(item);
          return (
            <Box
              key={item.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: "1px solid",
                borderColor: filled ? "primary.main" : "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1,
                minWidth: 88,
                cursor: "pointer",
                bgcolor: filled ? "rgba(0,171,85,0.06)" : "transparent",
              }}
              onClick={() => setOpenLetter(letter)}
            >
              <Typography variant="h6" fontWeight={800} sx={{ minWidth: 20 }}>
                {letter}
              </Typography>
              <IconButton
                size="small"
                sx={{ color: filled ? "primary.main" : "text.secondary" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenLetter(letter);
                }}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      {activeItem ? (
        <Dialog
          open={Boolean(openLetter)}
          onClose={() => setOpenLetter(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            TIME — {openLetter}
            <IconButton
              onClick={() => setOpenLetter(null)}
              size="small"
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <TimeExclusionPopup
            key={`${openLetter}-${activeItem.id}`}
            letter={openLetter}
            initialItem={activeItem}
            onSave={(patch) => updateItem(activeItem.id, patch)}
          />
        </Dialog>
      ) : null}
    </Box>
  );
};

export default TimeWarranty;
